/** Inline таблица для редактирования вариантов товара. */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, Wand2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { VariantMatrixWizard } from "@/components/products/VariantMatrixWizard";
import { Button } from "@/components/ui/button";
import { useDeleteConfirm } from "@/hooks/useDeleteConfirm";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { api } from "@/lib/api";
import { PriceInput } from "@/components/shared/PriceInput";

interface Variant {
  id: number;
  sku: string;
  name: string;
  extra_price: number;
  stock_quantity: number;
  is_active: boolean;
  sort_order: number;
}

interface Props {
  productId: number;
  productSku?: string;
  variants: Variant[];
}

export function VariantsEditor({
  productId,
  productSku,
  variants,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [newVariant, setNewVariant] = useState({
    sku: "",
    name: "",
    extra_price: 0,
    stock_quantity: 0,
    is_active: true,
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["admin-product", productId] });

  const addMutation = useMutation({
    mutationFn: async (data: typeof newVariant) => {
      await api.post(`/products/${productId}/variants`, data);
    },
    onSuccess: () => {
      toast.success(t("productVariants.added"));
      setAdding(false);
      setNewVariant({
        sku: "",
        name: "",
        extra_price: 0,
        stock_quantity: 0,
        is_active: true,
      });
      invalidate();
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.error?.message ?? t("common.toast.errorOccurred"));
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, patch }: { id: number; patch: Partial<Variant> }) => {
      await api.patch(`/products/${productId}/variants/${id}`, patch);
    },
    onSuccess: invalidate,
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/products/${productId}/variants/${id}`);
    },
    onSuccess: () => {
      toast.success(t("common.toast.deleted"));
      invalidate();
    },
  });

  const { askDelete, dialog: deleteDialog } = useDeleteConfirm<number>({
    onConfirm: (id) => deleteMutation.mutateAsync(id),
    title: "Удалить вариант?",
  });

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold">Варианты ({variants.length})</h3>
        {!adding && (
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setWizardOpen(true)}
            >
              <Wand2 className="w-4 h-4 mr-1" />
              Сгенерировать матрицу
            </Button>
            <Button size="sm" onClick={() => setAdding(true)}>
              <Plus className="w-4 h-4 mr-1" />
              Добавить
            </Button>
          </div>
        )}
      </div>

      <VariantMatrixWizard
        productId={productId}
        baseSku={productSku ?? ""}
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
      />

      <div className="border rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>SKU</TableHead>
              <TableHead>Название</TableHead>
              <TableHead>Доплата</TableHead>
              <TableHead>Остаток</TableHead>
              <TableHead>Активен</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {variants.map((v) => (
              <TableRow key={v.id}>
                <TableCell>
                  <Input
                    defaultValue={v.sku}
                    onBlur={(e) =>
                      e.target.value !== v.sku &&
                      updateMutation.mutate({ id: v.id, patch: { sku: e.target.value } })
                    }
                  />
                </TableCell>
                <TableCell>
                  <Input
                    defaultValue={v.name}
                    onBlur={(e) =>
                      e.target.value !== v.name &&
                      updateMutation.mutate({ id: v.id, patch: { name: e.target.value } })
                    }
                  />
                </TableCell>
                <TableCell className="w-44">
                  <PriceInput
                    value={v.extra_price}
                    onChange={(extra_price) =>
                      extra_price != null &&
                      extra_price !== v.extra_price &&
                      updateMutation.mutate({ id: v.id, patch: { extra_price } })
                    }
                  />
                </TableCell>
                <TableCell className="w-24">
                  <Input
                    type="number"
                    defaultValue={v.stock_quantity}
                    onBlur={(e) => {
                      const n = parseInt(e.target.value, 10);
                      if (!isNaN(n) && n !== v.stock_quantity) {
                        updateMutation.mutate({ id: v.id, patch: { stock_quantity: n } });
                      }
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Switch
                    checked={v.is_active}
                    onCheckedChange={(is_active) =>
                      updateMutation.mutate({ id: v.id, patch: { is_active } })
                    }
                  />
                </TableCell>
                <TableCell>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => askDelete(v.id, v.sku || v.name)}
                  >
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}

            {adding && (
              <TableRow className="bg-accent/30">
                <TableCell>
                  <Input
                    placeholder="SKU"
                    value={newVariant.sku}
                    onChange={(e) =>
                      setNewVariant({ ...newVariant, sku: e.target.value })
                    }
                  />
                </TableCell>
                <TableCell>
                  <Input
                    placeholder={t("productVariants.namePlaceholder")}
                    value={newVariant.name}
                    onChange={(e) =>
                      setNewVariant({ ...newVariant, name: e.target.value })
                    }
                  />
                </TableCell>
                <TableCell>
                  <PriceInput
                    value={newVariant.extra_price}
                    onChange={(extra_price) =>
                      setNewVariant({ ...newVariant, extra_price: extra_price ?? 0 })
                    }
                  />
                </TableCell>
                <TableCell>
                  <Input
                    type="number"
                    value={newVariant.stock_quantity}
                    onChange={(e) =>
                      setNewVariant({
                        ...newVariant,
                        stock_quantity: parseInt(e.target.value, 10) || 0,
                      })
                    }
                  />
                </TableCell>
                <TableCell>
                  <Switch
                    checked={newVariant.is_active}
                    onCheckedChange={(is_active) =>
                      setNewVariant({ ...newVariant, is_active })
                    }
                  />
                </TableCell>
                <TableCell className="space-x-1">
                  <Button
                    size="sm"
                    onClick={() => addMutation.mutate(newVariant)}
                    disabled={!newVariant.sku || !newVariant.name}
                  >
                    OK
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setAdding(false)}>
                    ✗
                  </Button>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {deleteDialog}
    </div>
  );
}
